<script setup>
import { ref, onMounted, computed } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import TicketRegisterPage from '../../helpdesk/pages/TicketRegisterPage.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
  businessAccountId: {
    type: [Number, String],
    required: false,
  },
});

const store = useStore();
const { accountId } = useAccount();
const tickets = useMapGetter('tickets/getTickets');
const loading = computed(() => store.getters['tickets/getUIFlags'].isFetching);

const showCreateModal = ref(false);

const contactTickets = computed(() => {
  if (!tickets.value || !props.contactId) return [];

  // Filter by contact_id or business_account_id (if available)
  const contactId = Number(props.contactId);
  const businessAccountId = props.businessAccountId
    ? Number(props.businessAccountId)
    : null;

  return tickets.value.filter(
    ticket =>
      ticket.contact_id === contactId ||
      (businessAccountId && ticket.business_account_id === businessAccountId)
  );
});

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const openTicket = ticketId => {
  // Navigate to ticket view page
  window.open(
    `/app/accounts/${accountId.value}/helpdesk/ticket/${ticketId}`,
    '_blank'
  );
};

const openCreateModal = () => {
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
};

const onTicketCreated = () => {
  closeCreateModal();
  // Refresh tickets list
  store.dispatch('tickets/getTickets', { contact_id: props.contactId });
};

onMounted(() => {
  if (props.contactId) {
    store.dispatch('tickets/getTickets', { contact_id: props.contactId });
  }
});
</script>

<template>
  <div class="tickets-list p-2">
    <div class="mb-4">
      <Button color="blue" size="sm" class="w-full" @click="openCreateModal">
        {{ $t('CONVERSATION_SIDEBAR.TICKETS.CREATE_TICKET') }}
      </Button>
    </div>

    <div v-if="loading" class="flex justify-center py-4">
      <div
        class="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"
      />
    </div>

    <div
      v-else-if="contactTickets.length === 0"
      class="text-center py-6 text-slate-500"
    >
      <p>{{ $t('CONVERSATION_SIDEBAR.TICKETS.NO_TICKETS') }}</p>
    </div>

    <div v-else class="">
      <div
        v-for="ticket in contactTickets"
        :key="ticket.id"
        class="p-3 rounded-md cursor-pointer hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 border border-n-slate-3"
        @click="openTicket(ticket.id)"
      >
        <div class="flex items-start justify-between mb-2">
          <h4
            class="font-medium text-slate-500 text-n-slate-11 text-sm line-clamp-2"
          >
            {{ ticket.title }}
          </h4>
          <span class="text-xs text-slate-500 ml-2 flex-shrink-0">
            #{{ ticket.id }}
          </span>
        </div>

        <p class="text-xs text-slate-600 mb-3 line-clamp-2">
          {{ ticket.description }}
        </p>

        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <span
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
              :style="{
                backgroundColor: ticket.status_colors.background,
                color: ticket.status_colors.text,
              }"
            >
              {{ ticket.status_name || 'N/A' }}
            </span>
            <span
              class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
              :style="{
                backgroundColor: ticket.priority_colors.background,
                color: ticket.priority_colors.text,
              }"
            >
              {{ ticket.priority_name || 'N/A' }}
            </span>
          </div>

          <span class="text-xs text-slate-500">
            {{ formatDate(ticket.created_at) }}
          </span>
        </div>

        <div v-if="ticket.assignee" class="mt-2 text-xs text-slate-600">
          <span class="font-medium">Atribuído:</span> {{ ticket.assignee.name }}
        </div>
      </div>
    </div>

    <woot-modal
      v-model:show="showCreateModal"
      :on-close="closeCreateModal"
      modal-type="centered"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <div class="pt-6 px-8">
          <p class="text-xl font-bold text-n-slate-12">
            {{ $t('CONVERSATION_SIDEBAR.TICKETS.CREATE_TICKET') }}
          </p>
          <p class="text-n-slate-11 mt-1">
            {{ $t('CONVERSATION_SIDEBAR.TICKETS.CREATE_TICKET_DESC') }}
          </p>
        </div>
        <div class="overflow-hidden flex justify-center items-center">
          <TicketRegisterPage
            :contact-id="contactId"
            :conversation-id="conversationId"
            :fetch-contacts="false"
            @ticket-created="onTicketCreated"
            @cancel="closeCreateModal"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
